// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";

// models
import { BATCHOP_LOCATION_TAGS, BATCHOP_LOCATION_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import * as BusinessLogic from "@/lib/BusinessLogic";
import type { IBatchOperationGroup } from "@/lib/backend/batch_operations.generated.types.ts";

// subcomponents
import ErrorComponent from "@/components/ErrorComponent";
import Toggle from "react-toggle";
import { translate } from "@/components/IntlProviderWrapper";

// backend
import { getBackend } from "@/lib/backend/Backend2";

import {
    Columns,
    mapDataToCategories,
    mapDataToCustomTags,
    onChangeUpdateTags,
    updateTagsConfig,
    type TagsConfig,
    type TagUpdateValue,
} from "@/components/react/ReactTableTags";
import { BATCHOP_LOCATION_TAGS as MAN_BATCHOP_LOCATION_TAGS } from "@/lib/ManufacturingTagsEditor.generated";

// type definitions
import type { ITags } from "@/lib/Models";
import TagsTables from "@/components/react/TagsTables";

import UserDocumentationLink from "@/components/UserDocumentationLink";

import img_banner from "@/img/banner.png";

type ITagDisplay = {
    name: string;
    id: string;
};

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

export type State = {
    uuid: string;
    title: string;
    group_uuid: string;
    tags: ITags;
    groups: IBatchOperationGroup[];
    size: number;
    message: string;
    componentTitle: string;
    error: string;
    warning: string;
};

/**
 * Form to create new line.
 */
export default class BatchopLocationCreate extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            uuid: "",
            title: "",
            group_uuid: "",
            size: 0,
            tags: {},
            groups: [],
            message: "",
            componentTitle: translate("Manufacturing.BatchopLocations.add_new_location", "Add New Location"),
            error: "",
            warning: "",
        };
    }

    /**
     * Loading component data (plants).
     */
    async componentDidMount() {
        try {
            const groups = (await getBackend().batchOperations.getGroups({})).items;

            this.setState({
                groups,
                group_uuid: groups && groups[0] ? groups[0].uuid : "",
            });
        } catch (e: unknown) {
            this.setState({ error: (e as Error).message });
        }
    }

    /**
     * Basic form validation.
     */
    validateShallow() {
        return this.state.title.length > 0;
    }

    /**
     * Handling change of any input component.
     */
    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        let value: string | boolean = target.value;
        const name = target.name;

        // hack for toggle element
        if (Object.keys(BATCHOP_LOCATION_TAGS).includes(name)) {
            value = (target as HTMLInputElement).checked;
        }

        // handle line group and tags
        if (name === "line_group") {
            let tags = this.state.tags || {};
            tags.line_group = value.toString();
            this.setState({ tags });
        } else {
            // update new datasource placeholder
            if (Object.prototype.hasOwnProperty.call(this.state, name)) {
                this.setState(prevState => ({
                    ...prevState,
                    [name]: value,
                }));
            }

            if (Object.keys(BATCHOP_LOCATION_TAGS).includes(name)) {
                let tags = this.state.tags || {};
                tags[name] = value.toString();
                this.setState({ tags });
            }
        }
    };

    /**
     * Handling submission of the form. Basic checks and saves with error handling.
     */
    handleSubmit = async () => {
        try {
            if (this.validateShallow()) {
                let uuid = this.props.match.params.uuid;

                if (this.state.uuid === "") {
                    const response = await getBackend().batchOperations.createLocation({
                        disabled: false,
                        group_uuid: this.state.group_uuid,
                        tags: this.state.tags,
                        title: this.state.title,
                        size: this.state.size,
                    });
                    uuid = response.uuid;
                } else {
                    // update current object
                    await getBackend().batchOperations.updateLocation({
                        disabled: false,
                        uuid: this.state.uuid,
                        group_uuid: this.state.group_uuid,
                        tags: this.state.tags,
                        title: this.state.title,
                        size: this.state.size,
                    });
                }
                await BusinessLogic.forceManufacturingCacheRefresh();
                this.props.history.push(`/digital-twin/resources/batchop_locations/${uuid}`);
            } else {
                this.setState({
                    warning: translate(
                        "Manufacturing.Lines.cannot_submit_missing_values",
                        "Cannot submit a form with missing values."
                    ),
                });
            }
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
        }
    };

    onGuiChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    renderReactTable() {
        const tagsConfig = updateTagsConfig(MAN_BATCHOP_LOCATION_TAGS as TagsConfig);
        const tagCategories = mapDataToCategories(this.state.tags, tagsConfig, BATCHOP_LOCATION_TAGS_ACCESS);
        return (
            <React.Fragment>
                <div className="text-right mb-3">
                    <UserDocumentationLink link_key={"batchop_locations"} />
                </div>
                <TagsTables
                    categories={tagCategories}
                    columns={Columns}
                    onChangeCell={this.onGuiChangeCell}
                    customTags={mapDataToCustomTags(this.state.tags, tagsConfig)}
                />
            </React.Fragment>
        );
    }

    /**
     * Renders tag.
     * @param {ITagDisplay} tag
     */
    renderTagField(tag: ITagDisplay) {
        return (
            <div className="form-group" key={tag.id}>
                <label>{tag.name}</label>
                <br />
                <Toggle name={tag.id} checked={this.state[tag.id]} onChange={this.handleInputChange} />
            </div>
        );
    }

    /**
     * Rendering JSX for current component.
     */
    render() {
        // tags
        const tags = [
            /* { name: translate("Manufacturing.Lines.Tags.aging", "Aging"), id: BATCHOP_LOCATION_TAGS.aging }, */
        ];

        return (
            <div key="ds_new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    <h2 className="mb-4">{this.state.componentTitle}</h2>
                                    <ErrorComponent msg={this.state.error} type="error" />
                                    <ErrorComponent msg={this.state.warning} type="warning" />
                                    <div className="tab-content">
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.title" defaultMessage="Title" />:
                                            </label>
                                            <input
                                                name="title"
                                                type="text"
                                                className="form-control"
                                                value={this.state.title}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage
                                                    id="common.batchop_group"
                                                    defaultMessage="Batch Operations Group"
                                                />
                                                :
                                            </label>
                                            <select
                                                name="group_uuid"
                                                className="form-control"
                                                value={this.state.group_uuid}
                                                onChange={this.handleInputChange}
                                            >
                                                {this.state.groups.map((g, i) => {
                                                    return (
                                                        <option key={i} value={g.uuid}>
                                                            {g.name}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.size" defaultMessage="Size" />:
                                            </label>
                                            <input
                                                name="size"
                                                type="text"
                                                className="form-control"
                                                value={this.state.size}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>
                                    </div>
                                    {this.renderReactTable()}
                                    {tags.map(tag => this.renderTagField(tag))}
                                    <div className="button-list button-list-entity-edit order-last ml-auto">
                                        <button className="btn btn-primary" onClick={this.handleSubmit}>
                                            <FormattedMessage id="common.save" defaultMessage="Save" />
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => this.props.history.goBack()}
                                        >
                                            <FormattedMessage id="common.back" defaultMessage="Back" />
                                        </button>
                                    </div>
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}
